import React, { useContext } from 'react';
import type { FlattenSimpleInterpolation } from 'styled-components';
import styled, { css } from 'styled-components';
import { AudiCopy1WithHoverStyle, timeL, timeM, timeXL } from '../AudiHeader/AudiHeaderStyleUtils';
import type {
  AudiHeaderMenuButtonProps,
  MenuIconAnimatedLinesProps,
} from '../../interfaces/header-components.interfaces';
import {
  iconBottomCloseKeyframes,
  iconBottomOpenKeyframes,
  iconMiddleCloseKeyframes,
  iconMiddleOpenKeyframes,
  iconMiddleSecondCloseKeyframes,
  iconMiddleSecondOpenKeyframes,
  iconTopCloseKeyframes,
  iconTopOpenKeyframes,
} from './AudiHeaderMenuButtonKeyframes';
import type { HeaderStyledMenuButtonWrapperProps } from '../../interfaces/header-styled-components.interfaces';
import { NAVIGATION_CLASSNAME, NAVIGATION_MOBILE_CLASSNAME } from '../../utils/globals';
import { ServiceContext } from '../../context/ServiceContext';

const HeaderStyledMenuButtonWrapper = styled.div<HeaderStyledMenuButtonWrapperProps>`
  display: ${({ isLoading }): string => (isLoading ? 'none' : 'block')};
  flex-shrink: 0;
  order: -1;
  position: relative;
  width: 33%;

  ${({ isSmallView, theme: { breakpoints } }) =>
    !isSmallView &&
    `@media screen and (min-width: ${breakpoints.l}px) {
      display: none;
    }`}

  & button {
    background-color: transparent;
    border: 0 none;
    cursor: pointer;
    display: flex;
    font-size: 1em;
    margin: var(--one-header-space-l) 0;
    order: 1;
    padding: 0;
    text-align: left;
  }
`;

const StyledMenuButtonIcon = styled.div`
  height: 18px;
  margin: 1px var(--one-header-space-s) 3px 3px;
  position: relative;
  width: 18px;
`;

const StyledMenuButtonIconTopLine = styled.div<MenuIconAnimatedLinesProps>`
  background-color: ${({ theme }): string =>
    theme.type === 'legacy' ? 'var(--one-header-color-black)' : 'var(--one-header-color-white)'};
  height: 1px;
  left: 0;
  ${({ isOpen, theme }): FlattenSimpleInterpolation =>
    isOpen
      ? css`
          animation: ${iconTopOpenKeyframes} ${timeXL} forwards;
          animation-timing-function: ${theme.easing.easingDefault};
        `
      : css`
          animation: ${iconTopCloseKeyframes} ${timeXL};
        `}
  position: absolute;
  right: 0;
  top: 6px;
`;

const StyledMenuButtonIconMiddleLine = styled.div<MenuIconAnimatedLinesProps>`
  background-color: ${({ theme }): string =>
    theme.type === 'legacy' ? 'var(--one-header-color-black)' : 'var(--one-header-color-white)'};
  height: 1px;
  left: 0;
  ${({ isOpen, theme }): FlattenSimpleInterpolation =>
    isOpen
      ? css`
          animation: ${iconMiddleOpenKeyframes} ${timeL} forwards;
          animation-delay: ${timeM};
          animation-timing-function: ${theme.easing.easingDefault};
        `
      : css`
          animation: ${iconMiddleCloseKeyframes} ${timeL};
        `}
  position: absolute;
  right: 0;
  top: 9px;
`;

const StyledMenuButtonIconMiddleSecondLine = styled.div<MenuIconAnimatedLinesProps>`
  background-color: ${({ theme }): string =>
    theme.type === 'legacy' ? 'var(--one-header-color-black)' : 'var(--one-header-color-white)'};
  height: 1px;
  left: 0;
  ${({ isOpen, theme }): FlattenSimpleInterpolation =>
    isOpen
      ? css`
          animation: ${iconMiddleSecondOpenKeyframes} ${timeL} forwards;
          animation-delay: ${timeM};
          animation-timing-function: ${theme.easing.easingDefault};
        `
      : css`
          animation: ${iconMiddleSecondCloseKeyframes} ${timeL};
        `}
  position: absolute;
  right: 0;
  top: 9px;
`;

const StyledMenuButtonIconBottomLine = styled.div<MenuIconAnimatedLinesProps>`
  background-color: ${({ theme }): string =>
    theme.type === 'legacy' ? 'var(--one-header-color-black)' : 'var(--one-header-color-white)'};
  height: 1px;
  left: 0;
  ${({ isOpen, theme }): FlattenSimpleInterpolation =>
    isOpen
      ? css`
          animation: ${iconBottomOpenKeyframes} ${timeXL} forwards;
          animation-timing-function: ${theme.easing.easingDefault};
        `
      : css`
          animation: ${iconBottomCloseKeyframes} ${timeXL};
        `}
  position: absolute;
  right: 0;
  top: 12px;
`;

const StyledMenuButtonText = styled.span`
  color: ${({ theme }): string =>
    theme.type === 'legacy' ? 'var(--one-header-color-black)' : 'var(--one-header-color-white)'};
  ${({ theme }): FlattenSimpleInterpolation => AudiCopy1WithHoverStyle(theme)};
`;

const AudiHeaderMenuButton: React.FC<AudiHeaderMenuButtonProps> = ({
  label,
  headerNavigationItemsAmount,
  onClick,
  isLoading,
  isOpen,
}) => {
  const { headerStateService } = useContext(ServiceContext);

  let isSmallView = true;
  headerStateService.registerCallback((headerStateRemote) => {
    isSmallView = headerStateRemote.isMobileView;
  });
  return (
    <HeaderStyledMenuButtonWrapper
      headerNavigationItemsAmount={headerNavigationItemsAmount}
      isSmallView={isSmallView}
      isLoading={isLoading}
      isOpen={isOpen}
    >
      <button
        aria-label={label}
        onClick={onClick}
        type="button"
        className={`${NAVIGATION_CLASSNAME} ${NAVIGATION_MOBILE_CLASSNAME}`}
      >
        <StyledMenuButtonIcon>
          <StyledMenuButtonIconTopLine isOpen={isOpen} />
          <StyledMenuButtonIconMiddleLine isOpen={isOpen} />
          <StyledMenuButtonIconMiddleSecondLine isOpen={isOpen} />
          <StyledMenuButtonIconBottomLine isOpen={isOpen} />
        </StyledMenuButtonIcon>
        <StyledMenuButtonText>{label}</StyledMenuButtonText>
      </button>
    </HeaderStyledMenuButtonWrapper>
  );
};

export default AudiHeaderMenuButton;
